import { z } from "zod";

/** Campaign parameters read from the landing URL. */
export const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "utm_id",
] as const;

/** Ad-platform click identifiers read from the landing URL. */
export const CLICK_ID_KEYS = [
  "gclid",
  "gbraid",
  "wbraid",
  "fbclid",
  "msclkid",
  "ttclid",
  "li_fat_id",
  "twclid",
] as const;

const param = z.string().trim().max(500).optional();
const url = z.string().trim().max(2048).optional();

export const touchSchema = z
  .object({
    ...Object.fromEntries([...UTM_KEYS, ...CLICK_ID_KEYS].map((key) => [key, param])),
    landingPage: url,
    referrer: url,
    capturedAt: z.string().max(40).optional(),
  })
  .strip() as z.ZodType<Touch>;

export type Touch = Partial<
  Record<(typeof UTM_KEYS)[number] | (typeof CLICK_ID_KEYS)[number], string>
> & {
  landingPage?: string;
  referrer?: string;
  capturedAt?: string;
};

export const attributionSchema = z
  .object({
    firstTouch: touchSchema.optional(),
    lastTouch: touchSchema.optional(),
    fbc: param,
    fbp: param,
  })
  .strip();

export type Attribution = z.infer<typeof attributionSchema>;

export const sourceChannels = [
  "paid_search",
  "paid_social",
  "organic_search",
  "organic_social",
  "referral",
  "email",
  "direct",
  "other",
] as const;
export type SourceChannel = (typeof sourceChannels)[number];

export const sourceChannelLabels: Record<SourceChannel, string> = {
  paid_search: "Paid search",
  paid_social: "Paid social",
  organic_search: "Organic search",
  organic_social: "Organic social",
  referral: "Referral",
  email: "Email",
  direct: "Direct",
  other: "Other",
};

const SEARCH_ENGINES = /(^|\.)(google|bing|yahoo|duckduckgo|ecosia|baidu|yandex|startpage)\./i;
const SOCIAL_NETWORKS =
  /(^|\.)(facebook|fb|instagram|linkedin|lnkd|twitter|x|t|tiktok|pinterest|reddit|youtube)\.(com|co|in|me)$/i;
const PAID_SEARCH_MEDIUMS = new Set(["cpc", "ppc", "paid_search", "paidsearch", "sem"]);
const PAID_SOCIAL_MEDIUMS = new Set(["paid_social", "social_paid", "paidsocial", "cpm"]);

function hostOf(value: string | undefined): string | null {
  if (!value) return null;
  try {
    return new URL(value).hostname.replace(/^www\./, "");
  } catch {
    return null;
  }
}

/**
 * Classifies a touch into a marketing channel (docs §11.4). Click IDs win
 * over UTMs, which win over the referrer.
 */
export function deriveSourceChannel(touch: Touch | undefined, siteHost?: string): SourceChannel {
  if (!touch) return "direct";
  const medium = touch.utm_medium?.toLowerCase();

  if (touch.gclid || touch.gbraid || touch.wbraid || touch.msclkid) return "paid_search";
  if (touch.fbclid || touch.ttclid || touch.li_fat_id || touch.twclid) return "paid_social";
  if (medium && PAID_SEARCH_MEDIUMS.has(medium)) return "paid_search";
  if (medium && PAID_SOCIAL_MEDIUMS.has(medium)) return "paid_social";
  if (medium === "email" || medium === "newsletter") return "email";
  if (medium === "social" || medium === "organic_social") return "organic_social";
  if (medium === "referral") return "referral";
  if (touch.utm_source || medium) return "other";

  const referrer = hostOf(touch.referrer);
  if (!referrer || (siteHost && referrer === siteHost.replace(/^www\./, ""))) return "direct";
  if (SEARCH_ENGINES.test(referrer)) return "organic_search";
  if (SOCIAL_NETWORKS.test(referrer)) return "organic_social";
  return "referral";
}
